import { FC, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { initializeGA4, trackGA4PageView } from 'libs/ga4'
import { accessLogServices, storageServices } from 'services'
import {
  maskSensitiveQueryString,
  shouldTrackGA4PagePath,
  shouldTrackPagePath,
} from 'utils/pageViewTracking'

const isPageViewTrackingEnabled = () => {
  const value = process.env.REACT_APP_ENABLE_PAGE_VIEW_TRACKING
  if (!value) {
    return true
  }

  return value.toLowerCase() !== 'false'
}

/**
 * Track page-view for logged-in routes only.
 */
export const PageViewTracker: FC = () => {
  const location = useLocation()
  const lastTrackedUrlRef = useRef<string>('')

  useEffect(() => {
    initializeGA4()
  }, [])

  useEffect(() => {
    if (!isPageViewTrackingEnabled()) {
      return
    }

    if (!shouldTrackPagePath(location.pathname)) {
      return
    }

    const maskedQueryString = maskSensitiveQueryString(location.search)
    const normalizedUrl = `${location.pathname}${
      maskedQueryString ? `?${maskedQueryString}` : ''
    }`

    if (lastTrackedUrlRef.current === normalizedUrl) {
      return
    }

    lastTrackedUrlRef.current = normalizedUrl

    if (shouldTrackGA4PagePath(location.pathname)) {
      trackGA4PageView({
        pagePath: normalizedUrl,
        pageTitle: document.title || undefined,
      })
    }

    if (!storageServices.getAccessToken()) {
      return
    }

    const payload = {
      accessed_at: new Date().toISOString(),
      path: normalizedUrl,
      pathname: location.pathname,
      query_string: maskedQueryString,
      page_title: document.title || undefined,
    }

    accessLogServices.createPageViewAudit(payload).catch(() => {
      // Fail-safe: keep UX unaffected when logging fails.
    })
  }, [location.pathname, location.search])

  return null
}
